import { createContext, useContext } from 'react';
import { MessageDescriptor } from 'react-intl';

import { SearchRefinementInput } from '__generated__/globalTypes';
import { FilterTitle } from 'components/search/FilterTitle';
import { PrintablePlayerSortName } from 'contexts/config';

type Refinement = SearchRefinementInput;

type GQLCtx = {
  query: string;
  refineQuery: (newQuery: string) => void;
  activeFacets: {
    [attribute: string]: string[];
  };
  refine(attribute: string, cb: (prevValues: string[]) => string[]): void;
  refinements: Refinement[];
  advancedFilters: string;
  onSaleOnly: boolean;
  page: number;
  pages: number;
  facets: {
    [field: string]: {
      values: Array<{
        count: number;
        value: string;
      }>;
      max?: { value: string };
      min?: { value: string };
    };
  };
  sorts: PrintablePlayerSortName[];
};

export const GQLSearchContext = createContext<GQLCtx | null>(null);

export const useGQLSearchContext = () => {
  return useContext(GQLSearchContext);
};

export const useFacet = (attribute: string) => {
  const ctx = useContext(GQLSearchContext)!;

  const value = ctx.activeFacets[attribute] ?? [];

  return {
    refine: (newValues: string[]) => {
      ctx.refine(attribute, () => newValues);
    },
    value,
    items: (ctx.facets[attribute]?.values ?? []).map(e => ({
      ...e,
      label: e.value,
      isRefined: value.includes(e.value),
    })),
  };
};

export const useBackendPagination = () => {
  const ctx = useContext(GQLSearchContext)!;

  return {
    page: ctx.page,
    pages: ctx.pages,
    refine: (page: number) => {
      if (page === 0) {
        ctx.refine('p', () => []);
      } else {
        ctx.refine('p', () => [(page + 1).toString()]);
      }
    },
  };
};

const parseNum = (input: string | undefined) => {
  if (input != null) {
    return Number.parseInt(input, 10);
  }
  return undefined;
};

const format = (value: undefined | number, defaultV: undefined | number) => {
  if (value == null || value === defaultV) {
    return '';
  }
  return value.toString();
};

export const useBackendRangeSlider = ({
  attribute,
  min: minP,
  max: maxP,
}: {
  attribute: string;
  max?: number;
  min?: number;
}) => {
  const ctx = useContext(GQLSearchContext)!;

  const min =
    minP ?? (parseNum(ctx.facets[attribute]?.min?.value) as undefined | number);
  const max =
    maxP ?? (parseNum(ctx.facets[attribute]?.max?.value) as undefined | number);

  const ret = {
    refine: ([newMin, newMax]: [number | undefined, number | undefined]) => {
      if (!format(newMin, min).length && !format(newMax, max).length) {
        ctx.refine(attribute, () => []);
      } else {
        ctx.refine(attribute, () => [
          [format(newMin, min), format(newMax, max)].join(':'),
        ]);
      }
    },
    range: { min, max },
    start:
      ctx.activeFacets[attribute]?.flatMap(e =>
        e.split(':').map(ee => Number.parseInt(ee, 10))
      ) ?? [],
    canRefine: true,
  };

  return ret;
};

type TitleProps = {
  name: string;
  shortTitle?: MessageDescriptor;
  title: MessageDescriptor;
  short?: boolean;
};
export const GqlFilterTitle = ({
  name,
  shortTitle,
  title,
  short,
}: TitleProps) => {
  const { value } = useFacet(name);
  return (
    <FilterTitle
      name={(short && shortTitle) || title!}
      selected={value.length}
    />
  );
};
